import { AvatarModule } from 'ngx-avatars';
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment';
import { SharedService } from 'src/app/Services/sharedService';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { ScreencastActionModelComponent } from 'src/app/screencast/screencast-action-model/screencast-action-model.component';
import {
  ScreencastEventType,
  ScreencastSessionStatus,
  ScreencastSessionView,
} from '../audit-logs.types';

@Component({
  selector: 'app-audit-log-details',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AvatarModule,
  ],
  host: {
    class: 'block h-full w-full',
  },
  templateUrl: './audit-log-details.component.html',
  styleUrl: './audit-log-details.component.css',
})
export class AuditLogDetailsComponent {
  @Input() session: ScreencastSessionView | null = null;
  @Input() employeeName = '—';
  @Input() actorName = '—';
  @Input() employeeUser: any = null;
  @Input() actorUser: any = null;
  private readonly brokenAvatarUrls = new Set<string>();

  get resolvedEmployeeUser(): any {
    return this.employeeUser || this.session?.employeeUser || null;
  }

  get resolvedActorUser(): any {
    return this.actorUser || this.session?.adminUser || null;
  }

  constructor(
    private sharedService: SharedService,
    private dialog: MatDialog,
    private tokenEncryptionService: TokenEncryptionService,
  ) {}

  closeDrawer(): void {
    this.sharedService.componentDrawerSubject.next({
      componentName: 'audit-logs',
      componentData: null,
      componentAction: 'close',
      componentWidth: 0,
    });
  }

  getStatusLabel(status: ScreencastSessionStatus | undefined): string {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'COMPLETED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'INTERRUPTED':
        return 'Interrupted';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status: ScreencastSessionStatus | undefined): string {
    switch (status) {
      case 'ACTIVE':
        return 'bg-[#e8ecff] text-[#4146b5] border-[#cfd5ff]';
      case 'COMPLETED':
        return 'bg-[#d9f2dd] text-[#167a45] border-[#b9e2c2]';
      case 'FAILED':
        return 'bg-[#fef2f2] text-[#7f1d1d] border-[#ef4444]';
      case 'INTERRUPTED':
        return 'bg-[#fff5e6] text-[#b76b00] border-[#ffe0b3]';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  }

  getStatusIcon(status: ScreencastSessionStatus | undefined): string {
    switch (status) {
      case 'ACTIVE':
        return 'sensors';
      case 'COMPLETED':
        return 'check_circle';
      case 'FAILED':
        return 'error';
      case 'INTERRUPTED':
        return 'warning';
      default:
        return 'help_outline';
    }
  }

  getEventLabel(type: ScreencastEventType | null | undefined): string {
    switch (type) {
      case 'STARTED':
        return 'Screen-cast session started';
      case 'CONNECTED':
        return 'Screen-cast connected';
      case 'FAILED':
        return 'Connection failed';
      case 'ENDED':
        return 'Screen-cast session ended';
      case 'DROPPED':
        return 'Session interrupted';
      case 'HEARTBEAT':
        return 'Session heartbeat';
      default:
        return 'Screen-cast activity';
    }
  }

  getEventIcon(type: ScreencastEventType | null | undefined): string {
    switch (type) {
      case 'STARTED':
        return 'screen_share';
      case 'CONNECTED':
        return 'videocam';
      case 'FAILED':
        return 'error_outline';
      case 'ENDED':
        return 'stop_circle';
      case 'DROPPED':
        return 'link_off';
      default:
        return 'event';
    }
  }

  hasProfileImage(user: any): boolean {
    const url = this.getProfileImageUrl(user);
    return !!url && !this.brokenAvatarUrls.has(url);
  }

  markAvatarBroken(event: Event): void {
    const src = (event.target as HTMLImageElement)?.getAttribute('src');
    if (src) {
      this.brokenAvatarUrls.add(src);
    }
  }

  getProfileImageUrl(user: any): string | null {
    if (!user) {
      return null;
    }
    const candidates = [
      user.meta?.profile_image_url,
      user.meta?.profile_image,
      user.profile_image_url,
      user.profile_image,
    ];
    for (const url of candidates) {
      if (typeof url === 'string' && url.trim() && url !== 'null' && !/secure\.gravatar\.com/i.test(url)) {
        return url;
      }
    }
    return null;
  }

  formatReason(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const labels: Record<string, string> = {
      ADMIN_CLOSED: 'Admin closed',
      PERMISSION_DENIED: 'Permission denied',
      TIMEOUT: 'Timed out',
      NETWORK_ERROR: 'Network error',
      UNAVAILABLE: 'Unavailable',
      connection_failed: 'Connection failed',
      UNKNOWN: 'Unknown',
    };
    return labels[value] || value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }

  convertToLocal(dateStr: string | undefined | null): Date | null {
    if (!dateStr) {
      return null;
    }
    const local = moment.utc(dateStr).local();
    return local.isValid() ? local.toDate() : null;
  }

  formatDateTime(dateStr: string | undefined | null): string {
    const local = this.convertToLocal(dateStr);
    return local ? moment(local).format('MMM D, YYYY h:mm A') : '—';
  }

  formatDuration(): string {
    const start = this.session?.startedAtUtc || this.session?.connectedAtUtc;
    const end =
      this.session?.endedAtUtc ||
      this.session?.failedAtUtc ||
      this.session?.droppedAtUtc ||
      (this.session?.status === 'ACTIVE' ? moment.utc().format('YYYY-MM-DD HH:mm:ss') : this.session?.latestEventTimeUtc);
    if (!start || !end) {
      return '—';
    }
    const startMs = moment.utc(start).valueOf();
    const endMs = moment.utc(end).valueOf();
    const diff = endMs - startMs;
    if (!Number.isFinite(diff) || diff < 0) {
      return '—';
    }
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${seconds}s`;
  }

  networkSummary(): string | null {
    if (!this.session) {
      return null;
    }
    const parts: string[] = [];
    if (this.session.relayUsed !== null && this.session.relayUsed !== undefined) {
      parts.push(this.session.relayUsed ? 'TURN' : 'P2P');
    }
    if (this.session.rttMs !== null && this.session.rttMs !== undefined) {
      parts.push(`RTT ${this.session.rttMs} ms`);
    }
    if (this.session.packetLossPercent !== null && this.session.packetLossPercent !== undefined) {
      parts.push(`Loss ${this.session.packetLossPercent}%`);
    }
    if (this.session.jitterMs !== null && this.session.jitterMs !== undefined) {
      parts.push(`Jitter ${this.session.jitterMs} ms`);
    }
    return parts.length ? parts.join(' · ') : null;
  }

  timelineEvents() {
    return (this.session?.events || []).filter((event) => event.event_type !== 'HEARTBEAT');
  }

  viewScreencast(): void {
    const user = this.session?.employeeUser;
    if (!this.session?.canViewScreencast || !user?.id) {
      return;
    }
    const dbPath = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
    this.dialog.open(ScreencastActionModelComponent, {
      data: {
        userDetails: user,
        userId: user.id,
        dbPath,
      },
      panelClass: 'centered-dialog',
    });
  }
}
